import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { API } from "../../api/urls";
import { formatFileSize } from "./attachmentRules";

/**
 * Bir mesaja iliştirilmiş ekler.
 *
 * Dosyalar yetki kontrollü bir uçtan servis edilir; panel backend'e farklı bir
 * origin'den bağlandığı için <img src> doğrudan uca bağlanamaz (çerez taşımaz).
 * Bu yüzden görselin içeriği base64 (data URI) olarak çekilip <img src> ile basılır.
 *
 * İndirme ayrı uçtan yapılır: o yanıt sandbox başlıklarıyla (CSP sandbox, CORP,
 * Content-Disposition: attachment) gelir, tarayıcı içeriği render etmeye çalışmaz.
 */
const MessageAttachments = ({ ticketId, attachments }) => {
    const [previews, setPreviews] = useState({});
    const [zoomed, setZoomed] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const urlOf = useCallback(
        (attachmentId) => API.ticket_attachment(ticketId, attachmentId),
        [ticketId]
    );

    const base64UrlOf = useCallback(
        (attachmentId) => API.ticket_attachment_base64(ticketId, attachmentId),
        [ticketId]
    );

    // Görsel ekleri base64 olarak çek; data URI doğrudan <img src> olarak kullanılır.
    useEffect(() => {
        let cancelled = false;

        const loadPreviews = async () => {
            for (const attachment of attachments.filter(a => a.image)) {
                try {
                    const { data } = await axios.get(base64UrlOf(attachment.id));
                    if (cancelled) return;
                    const dataUri = data?.data?.dataUri;
                    if (dataUri) setPreviews(prev => ({ ...prev, [attachment.id]: dataUri }));
                } catch {
                    // Önizleme alınamazsa ek yine listelenir, sadece ikonla gösterilir.
                }
            }
        };

        loadPreviews();
        return () => {
            cancelled = true;
        };
    }, [attachments, base64UrlOf]);

    const downloadAttachment = async (attachment) => {
        setBusyId(attachment.id);
        try {
            const { data } = await axios.get(urlOf(attachment.id), { responseType: "blob" });
            const objectUrl = URL.createObjectURL(data);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = attachment.fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            toast.error(err.response?.status === 403
                ? "Bu dosyaya erişim yetkiniz yok."
                : "Dosya açılamadı.");
        } finally {
            setBusyId(null);
        }
    };

    const handleClick = (attachment) => {
        const preview = previews[attachment.id];
        // Resmin içeriği zaten indirilmiş durumda; büyütme anında açılır.
        if (attachment.image && preview) {
            setZoomed({ url: preview, name: attachment.fileName });
            return;
        }
        downloadAttachment(attachment);
    };

    if (!attachments || attachments.length === 0) return null;

    return (
        <>
            <div className="d-flex flex-wrap gap-2 mt-2">
                {attachments.map(attachment => {
                    const preview = previews[attachment.id];
                    return (
                        <button
                            key={attachment.id}
                            type="button"
                            onClick={() => handleClick(attachment)}
                            title={`${attachment.fileName} (${formatFileSize(attachment.sizeBytes)})`}
                            className="d-flex align-items-center gap-2 border rounded p-2 bg-white text-start"
                            style={{ cursor: "pointer", maxWidth: "100%" }}
                        >
                            {preview ? (
                                <img
                                    src={preview}
                                    alt={attachment.fileName}
                                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 4 }}
                                />
                            ) : (
                                <FeatherIcon
                                    icon={attachment.image ? "image" : "file-text"}
                                    size={22}
                                    className={attachment.image ? "text-secondary" : "text-danger"}
                                />
                            )}

                            <span style={{ minWidth: 0 }}>
                                <span
                                    className="d-block text-truncate"
                                    style={{ maxWidth: 180, fontSize: ".8rem", color: "#1f2a44" }}
                                >
                                    {attachment.fileName}
                                </span>
                                <span className="d-block text-muted" style={{ fontSize: ".72rem" }}>
                                    {busyId === attachment.id
                                        ? <Spinner size="sm" />
                                        : formatFileSize(attachment.sizeBytes)}
                                </span>
                            </span>
                        </button>
                    );
                })}
            </div>

            <Modal isOpen={!!zoomed} toggle={() => setZoomed(null)} size="lg" centered>
                <ModalHeader toggle={() => setZoomed(null)} className="py-2">
                    <span style={{ fontSize: ".9rem" }}>{zoomed?.name}</span>
                </ModalHeader>
                <ModalBody className="text-center p-2">
                    {zoomed && (
                        <img
                            src={zoomed.url}
                            alt={zoomed.name}
                            style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }}
                        />
                    )}
                </ModalBody>
            </Modal>
        </>
    );
};

MessageAttachments.propTypes = {
    ticketId: PropTypes.string.isRequired,
    attachments: PropTypes.array,
};

MessageAttachments.defaultProps = {
    attachments: [],
};

export default MessageAttachments;
